/**
 * snapMode.ts — Snap drawing points to nearest candle OHLC values.
 *
 * Usage:
 *   Import findNearestOHLC and call it when snapMode is enabled
 *   in the drawing store to snap overlay point placement.
 */

import type { Chart as KChart } from "klinecharts";

interface SnapResult {
  timestamp: number;
  value: number;
}

/**
 * Find the nearest OHLC value to the given data coordinates.
 *
 * Strategy:
 *   1. Get all candle data from the chart via getDataList()
 *   2. Find the nearest candle by timestamp
 *   3. Pick whichever of O/H/L/C is closest to the given value
 *
 * @param dataTimestamp - The timestamp at the cursor position
 * @param dataValue - The price value at the cursor position
 * @param chart - The KLineChart instance
 * @returns Snapped { timestamp, value } or null if no candle found
 */
export function findNearestOHLC(
  dataTimestamp: number,
  dataValue: number,
  chart: KChart,
): SnapResult | null {
  if (!chart) return null;

  try {
    const candles = chart.getDataList();
    if (!Array.isArray(candles) || candles.length === 0) return null;

    // Find nearest candle by timestamp
    let closestIndex = 0;
    let closestDist = Math.abs(
      (candles[0]?.timestamp ?? 0) - dataTimestamp,
    );

    for (let i = 1; i < candles.length; i++) {
      const ts = candles[i]?.timestamp;
      if (ts == null || !Number.isFinite(ts)) continue;
      const dist = Math.abs(ts - dataTimestamp);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    }

    const candle = candles[closestIndex];
    if (!candle || !Number.isFinite(candle.timestamp)) return null;

    // Find closest OHLC value to the given data value
    const ohlcValues: Array<{ value: number }> = [];

    if (Number.isFinite(candle.open)) ohlcValues.push({ value: candle.open });
    if (Number.isFinite(candle.high)) ohlcValues.push({ value: candle.high });
    if (Number.isFinite(candle.low)) ohlcValues.push({ value: candle.low });
    if (Number.isFinite(candle.close)) ohlcValues.push({ value: candle.close });

    if (ohlcValues.length === 0) return null;

    let bestValue = ohlcValues[0].value;
    let bestDist = Infinity;

    for (const ohlc of ohlcValues) {
      const dist = Math.abs(ohlc.value - dataValue);
      if (dist < bestDist) {
        bestDist = dist;
        bestValue = ohlc.value;
      }
    }

    return {
      timestamp: candle.timestamp,
      value: bestValue,
    };
  } catch {
    return null;
  }
}
